import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatDate, formatDateLong, formatCurrency } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Building2, Plus, Search, Pencil, Trash2, ArrowLeft, ChevronLeft, ChevronRight, Upload, Download, CheckCircle2, Package, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { validateEmail } from "@/lib/validation";
import CSVImportDialog from "@/components/CSVImportDialog";

const SA_BANKS_POPULAR = ["Standard Bank", "FNB (First National Bank)", "ABSA", "Nedbank", "Capitec Bank", "Discovery Bank"];
const SA_BANKS_OTHER = ["African Bank", "Bidvest Bank", "Investec", "TymeBank", "Bank Zero", "Sasfin Bank", "Mercantile Bank", "Grindrod Bank"];
const SA_BANKS_ALL = [...SA_BANKS_POPULAR, ...SA_BANKS_OTHER];

const emptyForm = {
  company_name: "", contact_person: "", email: "", phone: "",
  address: "", payment_terms: "", tax_vat_number: "",
  bank_account_name: "", bank_account_number: "", bank_name: "",
  status: "Active", notes: "", is_expense_supplier: false,
};

export default function SuppliersPage() {
  const { id } = useParams();
  if (id) return <SupplierDetail supplierId={id} />;
  return <SupplierList />;
}

function SupplierList() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activeCount, setActiveCount] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editSupplier, setEditSupplier] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const canEdit = ["admin", "sales_manager"].includes(user?.role);
  const canCreate = ["admin", "sales_manager", "sales_rep"].includes(user?.role);
  const canDelete = ["admin", "sales_manager"].includes(user?.role);

  const handleExport = async () => {
    try {
      const res = await api.get("/export/suppliers", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "suppliers_export.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Suppliers exported successfully");
    } catch { toast.error("Failed to export suppliers"); }
  };

  const fetchSuppliers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search, status: statusFilter, page, limit: 25, sort_by: "company_name", sort_order: "asc" };
      const res = await api.get("/suppliers", { params });
      setSuppliers(res.data.suppliers);
      setTotalPages(res.data.pages);
      setActiveCount(res.data.active_count);
    } catch { toast.error("Failed to load suppliers"); }
    finally { setLoading(false); }
  }, [search, statusFilter, page]);

  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

  useEffect(() => {
    if (searchParams.get("add") === "true" && canCreate) {
      setForm({ ...emptyForm });
      setEditSupplier(null);
      setDialogOpen(true);
    }
  }, [searchParams, canCreate]);

  const handleSave = async () => {
    if (!form.company_name) { toast.error("Company name is required"); return; }
    if (!form.email) { toast.error("Email is required"); return; }
    const emailCheck = validateEmail(form.email);
    if (!emailCheck.valid) { toast.error(emailCheck.error); return; }
    setSaving(true);
    try {
      if (editSupplier) {
        await api.put(`/suppliers/${editSupplier.supplier_id}`, form);
        toast.success("Supplier updated");
      } else {
        await api.post("/suppliers", form);
        toast.success("Supplier created");
      }
      setDialogOpen(false);
      fetchSuppliers();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/suppliers/${deleteId}`);
      toast.success("Supplier deleted");
      setDeleteId(null);
      fetchSuppliers();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  const openEdit = (s) => {
    setEditSupplier(s);
    setForm({ ...emptyForm, ...s });
    setDialogOpen(true);
  };

  const openAdd = () => {
    setEditSupplier(null);
    setForm({ ...emptyForm });
    setDialogOpen(true);
  };

  return (
    <div className="animate-fade-in" data-testid="suppliers-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl text-[#1F2937] tracking-tight">Suppliers</h1>
          <div className="text-sm text-gray-500 mt-1 flex items-center gap-2">
            <Badge className="bg-green-50 text-green-700 border-green-200 rounded-full text-xs">{activeCount} Active</Badge>
            <span>Manage your supplier network</span>
          </div>
        </div>
        {canCreate && (
          <div className="flex items-center gap-2">
            <Button data-testid="export-suppliers-button" variant="outline" onClick={handleExport} className="rounded-sm border-gray-300 text-gray-700 hover:bg-gray-50">
              <Download className="w-4 h-4 mr-1.5" /> Export CSV
            </Button>
            <Button data-testid="import-suppliers-button" variant="outline" onClick={() => setImportOpen(true)} className="rounded-sm border-gray-300 text-gray-700 hover:bg-gray-50">
              <Upload className="w-4 h-4 mr-1.5" /> Import CSV
            </Button>
            <Button data-testid="add-supplier-button" onClick={openAdd} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
              <Plus className="w-4 h-4 mr-1.5" /> Add New Supplier
            </Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input data-testid="supplier-search-input" placeholder="Search by company or contact..." value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-9 h-9 rounded-sm border-gray-300" />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger data-testid="supplier-status-filter" className="w-[150px] h-9 rounded-sm border-gray-300">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="Active">Active</SelectItem>
            <SelectItem value="Inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="border border-gray-200 rounded-sm shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Company</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Email</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Phone</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td colSpan={6} className="px-4 py-4"><div className="h-4 bg-gray-100 rounded animate-pulse" /></td>
                  </tr>
                ))
              ) : suppliers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-gray-400">
                    <Building2 className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium">No suppliers found</p>
                    <p className="text-xs mt-1">Add your first supplier to get started</p>
                  </td>
                </tr>
              ) : (
                suppliers.map((s) => (
                  <tr key={s.supplier_id} data-testid={`supplier-row-${s.supplier_id}`}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/suppliers/${s.supplier_id}`)}
                  >
                    <td className="px-4 py-3 font-medium text-[#1F2937]">{s.company_name}</td>
                    <td className="px-4 py-3 text-gray-600">{s.contact_person || "-"}</td>
                    <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{s.email}</td>
                    <td className="px-4 py-3 text-gray-600 hidden lg:table-cell">{s.phone || "-"}</td>
                    <td className="px-4 py-3">
                      <Badge className={`rounded-full text-xs ${s.status === "Active" ? "bg-green-50 text-green-700 border-green-200" : "bg-gray-50 text-gray-600 border-gray-200"}`}>
                        {s.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {canEdit && (
                          <Button data-testid={`edit-supplier-${s.supplier_id}`} variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-[#E63946]" onClick={() => openEdit(s)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                        )}
                        {canDelete && (
                          <Button data-testid={`delete-supplier-${s.supplier_id}`} variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-[#E63946]" onClick={() => setDeleteId(s.supplier_id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} className="h-8 rounded-sm">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="h-8 rounded-sm">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-semibold text-xl">{editSupplier ? "Edit Supplier" : "Add New Supplier"}</DialogTitle>
          </DialogHeader>
          <SupplierForm form={form} setForm={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="save-supplier-button" onClick={handleSave} disabled={saving} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
              {saving ? "Saving..." : "Save Supplier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete this supplier. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-supplier" onClick={handleDelete} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CSVImportDialog open={importOpen} onOpenChange={setImportOpen} entityType="suppliers" onComplete={fetchSuppliers} />
    </div>
  );
}

function SupplierForm({ form, setForm }) {
  const [emailErr, setEmailErr] = useState(null);
  const [emailOk, setEmailOk] = useState(false);
  const [showOtherBank, setShowOtherBank] = useState(form.bank_name && !SA_BANKS_ALL.includes(form.bank_name));

  const f = (field) => (e) => {
    const next = { ...form, [field]: e.target.value };
    if (field === "company_name" && (!form.bank_account_name || form.bank_account_name === form.company_name)) {
      next.bank_account_name = e.target.value;
    }
    setForm(next);
  };

  const checkEmail = () => {
    if (!form.email) { setEmailErr(null); setEmailOk(false); return; }
    const r = validateEmail(form.email);
    setEmailErr(r.valid ? null : r.error);
    setEmailOk(r.valid);
  };

  const handleBankChange = (v) => {
    if (v === "Other") { setShowOtherBank(true); setForm({ ...form, bank_name: "" }); }
    else { setShowOtherBank(false); setForm({ ...form, bank_name: v }); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
      <div>
        <label className="block text-sm font-medium text-[#374151] mb-1">Company Name <span className="text-[#E63946]">*</span></label>
        <Input data-testid="supplier-company-name" value={form.company_name} onChange={f("company_name")} placeholder="Enter company name" className="rounded-sm border-gray-300 h-10" />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#374151] mb-1">Contact Person</label>
        <Input data-testid="supplier-contact-person" value={form.contact_person} onChange={f("contact_person")} placeholder="Enter contact name" className="rounded-sm border-gray-300 h-10" />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#374151] mb-1">Email <span className="text-[#E63946]">*</span></label>
        <div className="relative">
          <Input data-testid="supplier-email" type="email" value={form.email} onChange={f("email")} onBlur={checkEmail}
            placeholder="email@company.com" className={`rounded-sm h-10 pr-8 ${emailErr ? "border-[#E63946]" : "border-gray-300"}`} />
          {emailOk && <CheckCircle2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#10B981]" />}
        </div>
        {emailErr && <p data-testid="supplier-email-error" className="text-xs text-[#E63946] mt-1">{emailErr}</p>}
      </div>
      <div>
        <label className="block text-sm font-medium text-[#374151] mb-1">Phone</label>
        <Input data-testid="supplier-phone" value={form.phone} onChange={f("phone")} placeholder="(012) 345-6789" className="rounded-sm border-gray-300 h-10" />
      </div>
      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-[#374151] mb-1">Address</label>
        <Input data-testid="supplier-address" value={form.address} onChange={f("address")} placeholder="Physical address" className="rounded-sm border-gray-300 h-10" />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#374151] mb-1">Payment Terms</label>
        <Select value={form.payment_terms} onValueChange={(v) => setForm({ ...form, payment_terms: v })}>
          <SelectTrigger data-testid="supplier-payment-terms" className="rounded-sm border-gray-300 h-10"><SelectValue placeholder="Select terms" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Cash on delivery">Cash on delivery</SelectItem>
            <SelectItem value="30 days">30 days</SelectItem>
            <SelectItem value="60 days">60 days</SelectItem>
            <SelectItem value="90 days">90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="block text-sm font-medium text-[#374151] mb-1">Tax/VAT Number</label>
        <Input data-testid="supplier-vat" value={form.tax_vat_number} onChange={f("tax_vat_number")} placeholder="VAT number" className="rounded-sm border-gray-300 h-10" />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#374151] mb-1">Bank Name</label>
        <Select value={SA_BANKS_ALL.includes(form.bank_name) ? form.bank_name : (showOtherBank ? "Other" : "")} onValueChange={handleBankChange}>
          <SelectTrigger data-testid="supplier-bank-name" className="rounded-sm border-gray-300 h-10"><SelectValue placeholder="Select bank" /></SelectTrigger>
          <SelectContent>
            <div className="px-2 py-1.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Popular Banks</div>
            {SA_BANKS_POPULAR.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            <div className="my-1 border-t border-gray-200" />
            <div className="px-2 py-1.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Other Banks</div>
            {SA_BANKS_OTHER.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            <div className="my-1 border-t border-gray-200" />
            <SelectItem value="Other">Other (custom entry)</SelectItem>
          </SelectContent>
        </Select>
        {showOtherBank && <Input data-testid="supplier-custom-bank" value={form.bank_name} onChange={f("bank_name")} placeholder="Enter bank name" className="rounded-sm border-gray-300 h-9 mt-2 text-sm" />}
      </div>
      <div>
        <label className="block text-sm font-medium text-[#374151] mb-1">Bank Account Name</label>
        <Input value={form.bank_account_name} onChange={f("bank_account_name")} placeholder="Defaults to supplier company name" className="rounded-sm border-gray-300 h-10" />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#374151] mb-1">Bank Account Number</label>
        <Input value={form.bank_account_number} onChange={f("bank_account_number")} placeholder="Account number" className="rounded-sm border-gray-300 h-10" />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#374151] mb-1">Status</label>
        <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
          <SelectTrigger data-testid="supplier-status" className="rounded-sm border-gray-300 h-10"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Active">Active</SelectItem>
            <SelectItem value="Inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input data-testid="supplier-is-expense" type="checkbox" checked={form.is_expense_supplier || false}
            onChange={(e) => setForm({ ...form, is_expense_supplier: e.target.checked })}
            className="rounded border-gray-300 text-[#E63946] focus:ring-[#E63946]" />
          <span className="text-sm font-medium text-[#374151]">This supplier provides goods/services that should be tracked as expenses</span>
        </label>
        <p className="text-xs text-gray-400 mt-0.5 ml-6">PO invoices from this supplier will prompt auto-creation of expenses</p>
      </div>
      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-[#374151] mb-1">Notes</label>
        <textarea data-testid="supplier-notes" value={form.notes} onChange={f("notes")} placeholder="Additional notes..."
          className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm h-20 resize-none focus:outline-none focus:ring-2 focus:ring-[#E63946] focus:ring-offset-1" />
      </div>
      {/* VAT Information */}
      <div className="md:col-span-2 border-t border-gray-200 pt-4 mt-2">
        <p className="text-sm font-semibold text-[#1F2937] mb-3">VAT Information</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
          <label className="flex items-center gap-2 cursor-pointer md:col-span-2">
            <input data-testid="supplier-vat-registered" type="checkbox" checked={form.vat_registered || false}
              onChange={(e) => setForm({ ...form, vat_registered: e.target.checked })}
              className="rounded border-gray-300 text-[#E63946] focus:ring-[#E63946]" />
            <span className="text-sm font-medium text-[#374151]">Supplier is VAT registered</span>
          </label>
          {form.vat_registered && (
            <>
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-1">VAT Number</label>
                <Input data-testid="supplier-vat-number" value={form.vat_number || ""} onChange={f("vat_number")} placeholder="4987654321" maxLength={10} className="rounded-sm border-gray-300 h-9" />
                <p className="text-[10px] text-gray-400 mt-0.5">10 digits starting with 4</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-1">Default VAT Code</label>
                <select data-testid="supplier-default-vat-code" value={form.default_vat_code || "standard"} onChange={(e) => setForm({ ...form, default_vat_code: e.target.value })}
                  className="w-full rounded-sm border border-gray-300 px-3 py-1.5 text-sm h-9 focus:outline-none focus:ring-2 focus:ring-[#E63946]">
                  <option value="standard">Standard Rate (15%)</option>
                  <option value="zero-rated">Zero-Rated (0%)</option>
                  <option value="exempt">Exempt</option>
                </select>
              </div>
            </>
          )}
          {!form.vat_registered && (
            <p className="text-xs text-amber-600 md:col-span-2">Non-VAT registered: Input VAT cannot be claimed on purchases from this supplier.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function SupplierDetail({ supplierId }) {
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [poStats, setPoStats] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = ["admin", "sales_manager"].includes(user?.role);
  const isManager = ["admin", "sales_manager"].includes(user?.role);

  useEffect(() => {
    api.get(`/suppliers/${supplierId}`).then((r) => {
      setSupplier(r.data);
      setForm({ ...emptyForm, ...r.data });
    }).catch(() => toast.error("Supplier not found")).finally(() => setLoading(false));
    if (["admin", "sales_manager"].includes(user?.role)) {
      api.get(`/suppliers/${supplierId}/po-stats`).then((r) => setPoStats(r.data)).catch(() => {});
    }
  }, [supplierId, user?.role]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/suppliers/${supplierId}`, form);
      setSupplier(res.data);
      setEditOpen(false);
      toast.success("Supplier updated");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="animate-pulse h-64 bg-gray-100 rounded-sm" />;
  if (!supplier) return <p className="text-gray-400">Supplier not found</p>;

  const fields = [
    ["Company Name", supplier.company_name], ["Contact Person", supplier.contact_person],
    ["Email", supplier.email], ["Phone", supplier.phone],
    ["Address", supplier.address], ["Payment Terms", supplier.payment_terms],
    ["Tax/VAT Number", supplier.tax_vat_number], ["Bank Account", supplier.bank_account_name],
    ["Account Number", supplier.bank_account_number], ["Bank Name", supplier.bank_name],
    ["Date Added", formatDateLong(supplier.created_at)], ["Last Updated", formatDateLong(supplier.updated_at)],
  ];

  const tabs = [
    { id: "details", label: "Details" },
    ...(isManager ? [
      { id: "purchase-orders", label: `Purchase Orders (${poStats?.total_pos || 0})` },
      { id: "quote-requests", label: `Quote Requests (${poStats?.total_qrs || 0})` },
      { id: "expenses", label: `Expenses (${poStats?.expenses?.length || 0})` },
    ] : []),
    { id: "communications", label: `Communications (${poStats?.communications?.length || 0})` },
  ];

  return (
    <div className="animate-fade-in" data-testid="supplier-detail">
      <div className="flex items-center gap-3 mb-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/suppliers")} className="h-8 w-8">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="font-heading font-bold text-2xl text-[#1F2937] flex items-center gap-2">
            {supplier.company_name}
            <Badge className={`rounded-full text-xs ${supplier.status === "Active" ? "bg-green-50 text-green-700 border-green-200" : "bg-gray-50 text-gray-600 border-gray-200"}`}>
              {supplier.status}
            </Badge>
            {supplier.is_expense_supplier && <Badge className="bg-amber-50 text-amber-700 border-amber-200 rounded-full text-[10px]">Expense Supplier</Badge>}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {isManager && (
            <Button variant="outline" size="sm" onClick={() => navigate(`/purchase-orders?supplier=${supplierId}`)} className="rounded-sm text-xs">
              <Package className="w-3.5 h-3.5 mr-1" /> New PO
            </Button>
          )}
          {canEdit && (
            <Button data-testid="edit-supplier-detail" onClick={() => setEditOpen(true)} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
              <Pencil className="w-4 h-4 mr-1.5" /> Edit
            </Button>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      {isManager && poStats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <Card className="border border-gray-200 rounded-sm shadow-sm"><CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wider text-gray-400">Total PO Value</p>
            <p className="text-lg font-bold text-[#E63946]">{formatCurrency(poStats.total_po_value)}</p>
          </CardContent></Card>
          <Card className="border border-gray-200 rounded-sm shadow-sm"><CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wider text-gray-400">Active POs</p>
            <p className="text-lg font-bold text-[#1F2937]">{poStats.active_pos}</p>
          </CardContent></Card>
          <Card className="border border-gray-200 rounded-sm shadow-sm"><CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wider text-gray-400">Avg Delivery Time</p>
            <p className="text-lg font-bold text-[#1F2937]">{poStats.avg_delivery_days > 0 ? `${poStats.avg_delivery_days} days` : "—"}</p>
          </CardContent></Card>
          <Card className="border border-gray-200 rounded-sm shadow-sm"><CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wider text-gray-400">Total POs</p>
            <p className="text-lg font-bold text-[#1F2937]">{poStats.total_pos}</p>
          </CardContent></Card>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 mb-4">
        {tabs.map((tab) => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id ? "border-[#E63946] text-[#E63946]" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "details" && (
        <Card className="border border-gray-200 rounded-sm shadow-sm">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {fields.map(([label, value]) => (
                <div key={label}>
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">{label}</p>
                  <p className="text-sm text-[#1F2937]">{value || "-"}</p>
                </div>
              ))}
            </div>
            {supplier.notes && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">Notes</p>
                <p className="text-sm text-[#1F2937] whitespace-pre-wrap">{supplier.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === "purchase-orders" && poStats && (
        <Card className="border border-gray-200 rounded-sm shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-gray-50/50">
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">PO #</th>
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Date</th>
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Expected</th>
                <th className="p-3 text-right text-xs font-semibold uppercase text-gray-500">Total</th>
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Status</th>
              </tr></thead>
              <tbody>
                {(poStats.purchase_orders || []).length === 0 ? (
                  <tr><td colSpan={5} className="p-6 text-center text-gray-400">No purchase orders yet</td></tr>
                ) : poStats.purchase_orders.map((po) => (
                  <tr key={po.po_id} className="border-b border-gray-100 hover:bg-gray-50/50 cursor-pointer" onClick={() => navigate(`/purchase-orders/${po.po_id}`)}>
                    <td className="p-3 font-mono font-semibold text-[#E63946] text-sm">{po.po_number}</td>
                    <td className="p-3 text-gray-500 text-xs">{formatDate(po.po_date)}</td>
                    <td className="p-3 text-xs">{po.expected_delivery_date ? formatDate(po.expected_delivery_date) : "—"}</td>
                    <td className="p-3 text-right font-semibold">{formatCurrency(po.total)}</td>
                    <td className="p-3"><Badge className={`text-xs rounded-full border px-2 py-0.5 ${
                      po.status === "Fulfilled" ? "bg-green-50 text-green-700 border-green-200" :
                      po.status === "Sent" ? "bg-blue-50 text-blue-700 border-blue-200" :
                      po.status === "Partially Fulfilled" ? "bg-amber-50 text-amber-700 border-amber-200" :
                      po.status === "Cancelled" ? "bg-red-50 text-red-500 border-red-200" :
                      "bg-gray-100 text-gray-700 border-gray-300"}`}>{po.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {activeTab === "quote-requests" && poStats && (
        <Card className="border border-gray-200 rounded-sm shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-gray-50/50">
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">QR #</th>
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Date</th>
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Due Date</th>
                <th className="p-3 text-center text-xs font-semibold uppercase text-gray-500">Products</th>
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Status</th>
              </tr></thead>
              <tbody>
                {(poStats.quote_requests || []).length === 0 ? (
                  <tr><td colSpan={5} className="p-6 text-center text-gray-400">No quote requests yet</td></tr>
                ) : poStats.quote_requests.map((qr) => (
                  <tr key={qr.qr_id} className="border-b border-gray-100 hover:bg-gray-50/50 cursor-pointer" onClick={() => navigate(`/quote-requests/${qr.qr_id}`)}>
                    <td className="p-3 font-mono font-semibold text-[#E63946] text-sm">{qr.qr_number}</td>
                    <td className="p-3 text-gray-500 text-xs">{formatDate(qr.request_date)}</td>
                    <td className="p-3 text-xs">{qr.due_date ? formatDate(qr.due_date) : "—"}</td>
                    <td className="p-3 text-center">{qr.products_count}</td>
                    <td className="p-3"><Badge className={`text-xs rounded-full border px-2 py-0.5 ${
                      qr.status === "Quote Received" ? "bg-green-50 text-green-700 border-green-200" :
                      qr.status === "Sent" ? "bg-blue-50 text-blue-700 border-blue-200" :
                      qr.status === "Cancelled" ? "bg-red-50 text-red-500 border-red-200" :
                      qr.status === "Expired" ? "bg-amber-50 text-amber-700 border-amber-200" :
                      "bg-gray-100 text-gray-700 border-gray-300"}`}>{qr.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}


      {activeTab === "expenses" && poStats && (
        <Card className="border border-gray-200 rounded-sm shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-gray-50/50">
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Expense #</th>
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Description</th>
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Date</th>
                <th className="p-3 text-right text-xs font-semibold uppercase text-gray-500">Amount</th>
                <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Status</th>
              </tr></thead>
              <tbody>
                {(poStats.expenses || []).length === 0 ? (
                  <tr><td colSpan={5} className="p-6 text-center text-gray-400">No expenses linked to this supplier</td></tr>
                ) : poStats.expenses.map((exp) => (
                  <tr key={exp.expense_id} className="border-b border-gray-100 hover:bg-gray-50/50 cursor-pointer" onClick={() => navigate("/expenses")}>
                    <td className="p-3 font-mono font-semibold text-[#E63946] text-sm">{exp.expense_number}{exp.linked_po_number && <span className="text-gray-400 text-[10px] ml-1">({exp.linked_po_number})</span>}</td>
                    <td className="p-3 text-gray-700 text-xs max-w-[250px] truncate">{exp.description}</td>
                    <td className="p-3 text-gray-500 text-xs">{formatDate(exp.expense_date)}</td>
                    <td className="p-3 text-right font-semibold">{formatCurrency(exp.amount)}</td>
                    <td className="p-3"><Badge className={`text-xs rounded-full border px-2 py-0.5 ${
                      exp.status === "Approved" ? "bg-green-50 text-green-700 border-green-200" :
                      exp.status === "Submitted" ? "bg-blue-50 text-blue-700 border-blue-200" :
                      exp.status === "Rejected" ? "bg-red-50 text-red-500 border-red-200" :
                      "bg-gray-100 text-gray-700 border-gray-300"}`}>{exp.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {activeTab === "communications" && poStats && (
        <Card className="border border-gray-200 rounded-sm shadow-sm">
          <CardContent className="p-5">
            {(poStats.communications || []).length === 0 ? (
              <p className="text-center text-gray-400 py-6">No communications yet</p>
            ) : (
              <div className="space-y-3">
                {poStats.communications.map((c) => (
                  <div key={c.comm_id} className="flex items-start gap-3 border-b border-gray-100 pb-3 last:border-0">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#E63946] mt-2 shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-700">{c.subject || c.type}</span>
                        {c.auto_logged && <Badge data-testid={`auto-logged-${c.comm_id}`} className="bg-blue-50 text-blue-600 border border-blue-200 text-[9px] rounded-full">Auto-logged</Badge>}
                        {c.auto_generated && !c.auto_logged && <Badge className="bg-gray-100 text-gray-500 text-[9px] rounded-full">Auto</Badge>}
                      </div>
                      {c.notes && <p className="text-xs text-gray-500 mt-0.5">{c.notes}</p>}
                      {c.linked_records && Object.keys(c.linked_records).length > 0 && (
                        <div className="flex items-center gap-1 mt-1 flex-wrap">
                          {Object.entries(c.linked_records).map(([key, val]) => (
                            <Badge key={key} data-testid={`linked-record-${key}`} className="bg-indigo-50 text-indigo-600 border border-indigo-200 text-[9px] rounded-full">{val}</Badge>
                          ))}
                        </div>
                      )}
                      <p className="text-[10px] text-gray-400 mt-0.5">{c.created_by_name} — {formatDateLong(c.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading font-semibold text-xl">Edit Supplier</DialogTitle></DialogHeader>
          <SupplierForm form={form} setForm={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
